/*
 * AUTO_APPLY_TOOLS — deklarative Tool-Map fuer Phase-3 Auto-Apply (Review v1.4 #5).
 *
 * Jeder Eintrag beschreibt vollstaendig, wie ein propose_*-Event vom Server
 * (siehe RealAgentTransport._dispatch) auf eine EditorModel-Mutation und das
 * darauffolgende reportActionDone-Verdict abgebildet wird. Die ReviewController-
 * Engine `_runAutoApply` treibt die Tabelle generisch — neue Tools (z.B. fuer
 * Phase 4 update_context_info / set_context_link) sind hier ein 10-Zeilen-
 * Eintrag, kein neuer applyXxx-Handler im Controller.
 *
 * Konsumenten:
 *   - js/editor/ReviewController.js  : Engine + Prototyp-Generierung der applyXxx
 *   - js/core/CoreConsole.js         : _buildAutoApplyHandlers leitet die
 *                                      onProposeXxx-Handler aus dieser Tabelle ab
 *
 * Spec: docs/archive/ai-agent-specification.md §6 (Tool-Architektur).
 */

/**
 * @typedef {object} AutoApplySpec
 * @property {string} event
 *   SSE-Event-Name (z.B. 'propose_rewrite_apply'). Identisch zum Switch-Case
 *   in RealAgentTransport._dispatch.
 * @property {string} handlerName
 *   PascalCase-Variante. Treibt zwei Konventionen:
 *   - applyXxx-Methode auf ReviewController.prototype (`apply${handlerName}`)
 *   - onProposeXxx-Handler in CoreConsole (`onPropose${handlerName}`)
 * @property {string|null} modelMethod
 *   Methodenname auf EditorModel (z.B. 'replaceParagraphText'). null fuer
 *   Server-State-Tools wie `update_user_memory`, die KEINE Editor-Mutation
 *   ausloesen — die Engine ueberspringt den Apply und ack-t direkt mit
 *   `applied`.
 * @property {string|null} versionField
 *   Property im Payload, deren Wert mit `payload.baseVersion` gegen die lokale
 *   Version verglichen wird. null = kein Versions-Check (Insert, Sketch,
 *   create/delete-Section haben keine baseVersion).
 * @property {(payload: object) => any[]} payloadAdapter
 *   Mappt das Payload auf die Argumentliste der modelMethod. Synchron.
 * @property {(res: any) => boolean} [okPredicate]
 *   Optional. Standard: `res ? res.ok !== false : false`. Override fuer
 *   deleteParagraph, das in der Legacy-Variante undefined als Erfolg liefert.
 * @property {(res: any, payload: object, rc: object) => object} [okExtras]
 *   Extras fuer den 'applied'-Ack. Erhaelt Zugriff auf den ReviewController,
 *   damit appliedVersion via rc._readVersion(...) gelesen werden kann.
 * @property {(res: any, payload: object) => object} [errorExtras]
 *   Extras fuer den 'rejected'-Ack. Tool-spezifische Felder wie paragraphCount
 *   (DeleteSection im section_not_empty-Reject).
 * @property {(res: any, payload: object) => string} successLog
 *   Text fuer panel.logToolStatus bei Erfolg.
 * @property {string} failLogPrefix
 *   Praefix fuer panel.logToolError bei Fehlerpfad. Reason wird angehaengt.
 * @property {string|null} versionMismatchLog
 *   Text fuer panel.logToolStatus bei version_mismatch. null wenn unreachable
 *   (versionField === null).
 */

export const AUTO_APPLY_TOOLS = [
    {
        event: 'propose_rewrite_apply',
        handlerName: 'RewriteApply',
        modelMethod: 'replaceParagraphText',
        versionField: 'paragraphId',
        payloadAdapter: (p) => [
            p.paragraphId,
            p.newText,
            {
                formats: p.newFormats || [],
                links: p.newLinks || [],
                headingLevel: p.newHeadingLevel || 0,
                listType: p.newListType || null,
                listLevel: p.newListLevel || 0,
                blockquote: !!p.newBlockquote,
                codeBlock: !!p.newCodeBlock,
                moveCursor: false
            }
        ],
        okExtras: (res, p, rc) => ({
            appliedVersion: rc._readVersion(p.paragraphId)
        }),
        successLog: () => 'Applied background rewrite directly.',
        failLogPrefix: 'Background rewrite failed',
        versionMismatchLog: 'Background rewrite skipped — paragraph changed concurrently.'
    },
    {
        event: 'propose_insert_apply',
        handlerName: 'InsertApply',
        modelMethod: 'insertParagraphAfter',
        versionField: null,
        payloadAdapter: (p) => [
            p.afterParagraphId ?? null,
            p.newText,
            {
                formats: p.newFormats || [],
                links: p.newLinks || [],
                headingLevel: p.newHeadingLevel || 0,
                listType: p.newListType || null,
                listLevel: p.newListLevel || 0,
                blockquote: !!p.newBlockquote,
                codeBlock: !!p.newCodeBlock,
                moveCursor: false
            }
        ],
        okExtras: (res, p, rc) => ({
            newParagraphId: res.newParagraphId,
            appliedVersion: rc._readVersion(res.newParagraphId)
        }),
        successLog: () => 'Applied background insert directly.',
        failLogPrefix: 'Background insert failed',
        versionMismatchLog: null
    },
    {
        event: 'propose_delete_apply',
        handlerName: 'DeleteApply',
        modelMethod: 'deleteParagraph',
        versionField: 'paragraphId',
        // reviewApply: true wird durchgereicht, damit Cmd+Z den Cursor an die
        // geloeschte Stelle setzt (Review v1.4 #17). Konsequent in Adapter,
        // damit die Engine frei von Tool-Spezifika bleibt.
        payloadAdapter: (p) => [
            p.paragraphId,
            { reviewApply: true, moveCursor: false }
        ],
        // deleteParagraph kann undefined zurueckgeben (Legacy-Pfad) — als ok
        // werten, sonst wuerde jeder erfolgreiche Delete als rejected ack-en.
        okPredicate: (res) => res ? res.ok !== false : true,
        okExtras: () => ({}),
        successLog: () => 'Applied background delete directly.',
        failLogPrefix: 'Background delete failed',
        versionMismatchLog: 'Background delete skipped — paragraph changed concurrently.'
    },
    {
        event: 'propose_sketch',
        handlerName: 'Sketch',
        modelMethod: 'insertSketchAfter',
        versionField: null,
        payloadAdapter: (p) => [
            p.separatorId,
            p.afterParagraphId ?? null,
            (p.items || []).map(it => ({
                outlineDepth: it.outlineDepth,
                note: it.note,
                noteFormats: it.noteFormats || [],
                noteLinks: it.noteLinks || []
            })),
            { moveCursor: false }
        ],
        okExtras: (res) => ({ paragraphIds: res.paragraphIds }),
        // reason ist optional vom Agent mitgeliefert — im Log anzeigen, damit
        // der Auto-Apply fuer den Autor nachvollziehbar bleibt.
        successLog: (res, p) => p.reason
            ? `Applied sketch (${res.paragraphIds.length} items) directly — ${p.reason}`
            : `Applied sketch (${res.paragraphIds.length} items) directly.`,
        failLogPrefix: 'Sketch failed',
        versionMismatchLog: null
    },
    {
        event: 'propose_write_note',
        handlerName: 'WriteNote',
        modelMethod: 'updateParagraphNote',
        versionField: 'paragraphId',
        // Selective-Fields: nur Felder uebergeben, die im Payload tatsaechlich
        // gesetzt sind. updateParagraphNote ist ein Patch, kein Replace.
        payloadAdapter: (p) => {
            const update = {}
            if (p.note != null) {
                update.note = p.note
                // note + Formate als Trio: wird gemeinsam ersetzt, damit
                // alte Formate nicht auf neuem Text-Offset haengen.
                update.noteFormats = p.noteFormats || []
                update.noteLinks = p.noteLinks || []
            }
            if (p.outlineDepth != null) update.outlineDepth = p.outlineDepth
            return [p.paragraphId, update]
        },
        okExtras: (res, p, rc) => ({
            appliedVersion: rc._readVersion(p.paragraphId)
        }),
        successLog: () => 'Applied note update directly.',
        failLogPrefix: 'Note update failed',
        versionMismatchLog: 'Note update skipped — paragraph changed concurrently.'
    },
    {
        event: 'propose_create_section',
        handlerName: 'CreateSection',
        modelMethod: 'createSection',
        versionField: null,
        // Zwei Anker-Modi (mutex; Server-Schema garantiert das schon):
        //   afterSeparatorId  → Mode 1: [Sep + initialPara]
        //   beforeParagraphId → Mode 2: nur Sep, vor existierendem Paragraph
        payloadAdapter: (p) => [
            {
                area: p.area,
                afterSeparatorId: p.afterSeparatorId ?? null,
                beforeParagraphId: p.beforeParagraphId ?? null,
                title: p.title,
                depth: p.depth,
                initialText: p.initialText ?? null,
                initialFormats: p.initialFormats || [],
                initialLinks: p.initialLinks || [],
                initialHeadingLevel: p.initialHeadingLevel || 0,
                initialListType: p.initialListType || null,
                initialListLevel: p.initialListLevel || 0,
                initialBlockquote: !!p.initialBlockquote,
                initialCodeBlock: !!p.initialCodeBlock
            },
            { moveCursor: false }
        ],
        // firstParagraphId existiert nur in Mode 1 — in Mode 2 wird das
        // Feld weggelassen (der existierende Anker-Paragraph IST der
        // erste Inhalt der neuen Section).
        okExtras: (res) => {
            const out = { separatorId: res.separatorId }
            if (res.firstParagraphId != null) out.firstParagraphId = res.firstParagraphId
            return out
        },
        successLog: (res, p) => `Applied new section "${p.title}" directly.`,
        failLogPrefix: 'Create section failed',
        versionMismatchLog: null
    },
    {
        event: 'propose_update_section',
        handlerName: 'UpdateSection',
        modelMethod: 'updateSection',
        versionField: 'separatorId',
        payloadAdapter: (p) => {
            const update = {}
            if (p.title != null) update.title = p.title
            if (p.depth != null) update.depth = p.depth
            return [p.separatorId, update]
        },
        okExtras: (res, p, rc) => ({
            appliedVersion: rc._readVersion(p.separatorId)
        }),
        successLog: () => 'Applied section update directly.',
        failLogPrefix: 'Update section failed',
        versionMismatchLog: 'Section update skipped — separator changed concurrently.'
    },
    {
        event: 'propose_delete_section',
        handlerName: 'DeleteSection',
        modelMethod: 'deleteSection',
        versionField: null,
        payloadAdapter: (p) => [p.separatorId, { moveCursor: false }],
        okExtras: () => ({}),
        // Section-not-empty-Reject liefert paragraphCount mit, damit der
        // Server-Tool-Handler dem Modell den Grund klar mitteilen kann
        // (siehe Review v1.4 #11/#15).
        errorExtras: (res) => (res?.paragraphCount != null
            ? { paragraphCount: res.paragraphCount }
            : {}),
        successLog: () => 'Applied section delete directly.',
        failLogPrefix: 'Delete section failed',
        versionMismatchLog: null
    },
    {
        // Anker-basierter Section-Move (move-tool-specification.md).
        // Kein Versions-Check — ein Move aendert keinen Feldinhalt; das
        // Model re-validiert Ranges + Locks und liefert bei Konflikt einen
        // Reason-Code, der via diagnostics zum Server-Tool zurueckfliesst.
        event: 'propose_move_section',
        handlerName: 'MoveSection',
        modelMethod: 'moveSectionTo',
        versionField: null,
        payloadAdapter: (p) => [
            p.separatorId,
            {
                afterSeparatorId: p.afterSeparatorId ?? null,
                beforeSeparatorId: p.beforeSeparatorId ?? null
            }
        ],
        okExtras: (res) => (res?.noChange ? { noChange: true } : {}),
        successLog: (res, p) => (res?.noChange
            ? 'Section move skipped — already in place.'
            : `Moved section "${p.title || p.separatorId}" directly.`),
        failLogPrefix: 'Section move failed',
        versionMismatchLog: null
    },
    {
        // Anker-basierter Paragraph-/Subtree-Move (move-tool-specification.md).
        event: 'propose_move_paragraph',
        handlerName: 'MoveParagraph',
        modelMethod: 'moveParagraphTo',
        versionField: null,
        payloadAdapter: (p) => [
            p.paragraphId,
            {
                afterParagraphId: p.afterParagraphId ?? null,
                beforeParagraphId: p.beforeParagraphId ?? null
            }
        ],
        okExtras: (res) => {
            const out = { movedCount: res?.movedCount ?? 1 }
            if (res?.noChange) out.noChange = true
            return out
        },
        successLog: (res) => (res?.noChange
            ? 'Paragraph move skipped — already in place.'
            : `Moved ${res?.movedCount ?? 1} paragraph(s) directly.`),
        failLogPrefix: 'Paragraph move failed',
        versionMismatchLog: null
    },
    {
        // Phase 4 Sub-Phase 4.1: User-Memory-Schreibvorgang.
        //
        // Sonderpfad: modelMethod=null → Engine ueberspringt den Editor-
        // Apply (User-Memory hat keine Editor-Repraesentation). Der Server
        // schreibt die DB selbst, der Client ack-t nur fuer Console-Log.
        event: 'propose_update_user_memory',
        handlerName: 'UpdateUserMemory',
        modelMethod: null,
        versionField: null,
        payloadAdapter: () => [],
        okExtras: (_res, p) => (p.byteLength != null
            ? { byteLength: p.byteLength }
            : {}),
        successLog: (_res, p) => (p.byteLength != null
            ? `Updated user memory (${p.byteLength} bytes).`
            : 'Updated user memory.'),
        failLogPrefix: 'User memory update failed',
        versionMismatchLog: null
    },
    {
        // Phase 4 Sub-Phase 4.2: Context-Info-Schreibvorgang.
        //
        // EditorModel.updateParagraphInfo erwartet eine flache Update-Map
        // mit info + infoFormats + infoLinks (Trio, gemeinsam ersetzt —
        // alte Spans waeren sonst auf neuem Offset). set_context_link
        // teilt sich diesen Eintrag (Variante A); der Server berechnet
        // den Link-Patch und emittiert dasselbe SSE-Event.
        event: 'propose_update_context_info',
        handlerName: 'UpdateContextInfo',
        modelMethod: 'updateParagraphInfo',
        versionField: 'paragraphId',
        payloadAdapter: (p) => [p.paragraphId, {
            info: p.info ?? '',
            infoFormats: p.infoFormats || [],
            infoLinks: p.infoLinks || []
        }],
        okExtras: (res, p, rc) => ({
            appliedVersion: rc._readVersion(p.paragraphId)
        }),
        successLog: () => 'Applied context info update directly.',
        failLogPrefix: 'Context info update failed',
        versionMismatchLog: 'Context info update skipped — paragraph changed concurrently.'
    },
    {
        // Phase 4 Sub-Phase 4.2: Section-Sprung des Agent.
        //
        // Lokale UI-Aktion: kein Versions-Check (springt nur Viewport),
        // kein Lock-Check (auch gelockte Sections duerfen besucht werden).
        // EditorModel.jumpToSection delegiert an EditorInput._navigateToLink,
        // das Quell-/Zielposition in die NavigationHistory pushed (Alt+Pfeil-
        // Links bringt zurueck, identisch zum Section-Link-Klick).
        event: 'propose_jump_to_section',
        handlerName: 'JumpToSection',
        modelMethod: 'jumpToSection',
        versionField: null,
        payloadAdapter: (p) => [p.paragraphId],
        okExtras: (_res, p) => ({
            title: p.title || '',
            area: p.area || 'main'
        }),
        successLog: (_res, p) => `Jumped to "${p.title || p.paragraphId}".`,
        failLogPrefix: 'Jump failed',
        versionMismatchLog: null
    }
]

